import { FirebaseError } from "firebase/app";
import { multiFactor, MultiFactorInfo, TotpMultiFactorGenerator, User } from "firebase/auth";

export type MfaEnrollmentErrorKind =
  | "policy_required"
  | "policy_unavailable"
  | "invalid_code"
  | "restart_required"
  | "recent_login_required"
  | "token_expired"
  | "cancelled"
  | "rate_limited"
  | "signed_out"
  | "password_required"
  | "unsupported_provider"
  | "network"
  | "generic";

/**
 * English fallbacks, one per kind. Apps translate `kind` through their own i18n and must not
 * render these; they exist so `Error.message` stays readable in logs and Sentry, and so
 * surfaces without i18n (compose-ui, analysis-ui) have something to show.
 */
export const MFA_ENROLLMENT_FALLBACK_MESSAGES: Record<MfaEnrollmentErrorKind, string> = {
  policy_required: "Organization policy requires at least one enrolled authenticator.",
  policy_unavailable: "Could not check organization policy. Retry before removing an authenticator.",
  invalid_code: "That code is incorrect. Wait for a new code in your authenticator app and try again.",
  restart_required:
    "This setup can no longer be used. Restart setup and scan the new QR code in your authenticator app.",
  recent_login_required: "For your security, confirm your sign-in details and try again.",
  token_expired: "Your session was refreshed. Confirm your sign-in details to continue.",
  cancelled: "Confirmation was cancelled. Please try again when ready.",
  rate_limited: "Too many requests. Wait a moment and try again.",
  signed_out: "You are signed out. Sign in again to change your security settings.",
  password_required: "Enter your password to continue.",
  unsupported_provider: "This sign-in method cannot confirm your identity here.",
  network: "We could not reach the server. Check your connection and try again — nothing has been lost.",
  generic: "We could not update your security settings. Please try again.",
};

export class MfaEnrollmentError extends Error {
  readonly kind: MfaEnrollmentErrorKind;

  constructor(kind: MfaEnrollmentErrorKind, message: string = MFA_ENROLLMENT_FALLBACK_MESSAGES[kind]) {
    super(message);
    this.name = "MfaEnrollmentError";
    this.kind = kind;
  }
}

/** Kind of any thrown value, for surfaces that map kinds to their own copy. */
export function mfaEnrollmentErrorKind(error: unknown): MfaEnrollmentErrorKind {
  if (error && typeof error === "object" && "kind" in error) {
    const kind = (error as { kind: unknown }).kind;
    if (typeof kind === "string" && kind in MFA_ENROLLMENT_FALLBACK_MESSAGES) {
      return kind as MfaEnrollmentErrorKind;
    }
  }
  return "generic";
}

export type MfaEnrollmentFactor = {
  uid: string;
  displayName: string | null;
  enrollmentTime: string | null;
};

export const PASSWORD_PROVIDER_ID = "password";
export const GOOGLE_PROVIDER_ID = "google.com";
export const MICROSOFT_PROVIDER_ID = "microsoft.com";

export function getTotpEnrollmentFactors(user: User): MfaEnrollmentFactor[] {
  return multiFactor(user)
    .enrolledFactors.filter((factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID)
    .map((factor: MultiFactorInfo) => ({
      uid: factor.uid,
      displayName: factor.displayName ?? null,
      enrollmentTime: factor.enrollmentTime ?? null,
    }));
}

export function getReauthenticationProviderIds(user: User): string[] {
  const supported = new Set([PASSWORD_PROVIDER_ID, GOOGLE_PROVIDER_ID, MICROSOFT_PROVIDER_ID]);
  return [...new Set(user.providerData.map((provider) => provider.providerId).filter((id) => supported.has(id)))];
}

function isRestartRequiredServerMessage(message: string): boolean {
  return /TOO_MANY_ENROLLMENT_ATTEMPTS|INVALID_SESSION|SESSION_EXPIRED|SESSION_INFO|TOTP_CHALLENGE_TIMEOUT/i.test(
    message,
  );
}

export function mapMfaEnrollmentError(error: unknown): MfaEnrollmentError {
  // Already classified upstream (e.g. thrown deliberately by AuthContext) — keep the kind.
  if (error instanceof MfaEnrollmentError) {
    return error;
  }
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case "auth/invalid-verification-code":
        return new MfaEnrollmentError("invalid_code");
      case "auth/too-many-enrollment-attempts":
        return new MfaEnrollmentError("restart_required");
      case "auth/requires-recent-login":
        return new MfaEnrollmentError("recent_login_required");
      case "auth/user-token-expired":
      case "auth/invalid-user-token":
        return new MfaEnrollmentError("token_expired");
      case "auth/user-disabled":
        return new MfaEnrollmentError("signed_out");
      case "auth/missing-multi-factor-session":
      case "auth/invalid-multi-factor-session":
      case "auth/invalid-verification-id":
      case "auth/code-expired":
        return new MfaEnrollmentError("restart_required");
      case "auth/too-many-requests":
        return new MfaEnrollmentError("rate_limited");
      case "auth/popup-closed-by-user":
      case "auth/cancelled-popup-request":
        return new MfaEnrollmentError("cancelled");
      // A dropped connection is not a failure of the operation. Without this it lands on
      // `generic`, which sends the user to support for what is usually a few seconds of wifi.
      case "auth/network-request-failed":
        return new MfaEnrollmentError("network");
      case "auth/internal-error":
        if (isRestartRequiredServerMessage(error.message)) {
          return new MfaEnrollmentError("restart_required");
        }
        break;
    }
  }
  return new MfaEnrollmentError("generic");
}

/** Mistyped TOTP can be retried on the same QR. Restart-required errors need a new secret. */
export function isEnrollmentRestartRequired(error: unknown): boolean {
  return Boolean(error && typeof error === "object" && "kind" in error && error.kind === "restart_required");
}

/** @deprecated Use isEnrollmentRestartRequired */
export function canRetryEnrollmentWithSameSecret(error: unknown): boolean {
  return Boolean(error && typeof error === "object" && "kind" in error && error.kind === "invalid_code");
}
